import { Box, Typography, CircularProgress } from '@mui/material';
import { List, RefreshCw } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { globalSnackbar } from 'components/GlobalSnackbar';
import { YandexTrackerApi } from 'api/yandex-tracker';
import {
  Button as ShellButton,
  IconButton as ShellIconButton,
  EmptyState,
} from 'components/AppShell';
import type { YandexTrackerQueue } from 'model/yandex-tracker';

function QueuesTab(): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [queues, setQueues] = useState<YandexTrackerQueue[]>([]);
  const currentProject = useSelector(
    (state: { reducer: { project?: { id?: string } } }) => state.reducer.project
  );

  const fetchQueues = useCallback(async () => {
    setLoading(true);
    try {
      const response = await YandexTrackerApi.getQueues();
      setQueues(response ?? []);
    } catch (error) {
      const err = error as Error;
      globalSnackbar({
        message: `Ошибка загрузки очередей: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  const handleSyncQueues = async () => {
    if (!currentProject?.id) {
      globalSnackbar({ message: 'Не выбран проект', severity: 'warning' });
      return;
    }
    setSyncing(true);
    try {
      const res = await YandexTrackerApi.syncQueues({
        body: { project_id: currentProject.id },
      });
      globalSnackbar({
        message: `Синхронизировано: всего ${res.total}, создано ${res.created}, обновлено ${res.updated}`,
        severity: 'success',
      });
      await fetchQueues();
    } catch (error) {
      const err = error as Error;
      globalSnackbar({
        message: `Ошибка синхронизации очередей: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <ShellButton
          variant="primary"
          leftIcon={
            syncing ? (
              <CircularProgress size={12} sx={{ color: 'currentColor' }} />
            ) : (
              <RefreshCw size={14} strokeWidth={1.75} />
            )
          }
          onClick={handleSyncQueues}
          disabled={syncing || !currentProject?.id}
        >
          {syncing ? 'Синхронизация…' : 'Синхронизировать очереди'}
        </ShellButton>
        <ShellIconButton
          label="Обновить список"
          onClick={fetchQueues}
          disabled={loading}
          icon={
            loading ? (
              <CircularProgress size={14} sx={{ color: 'var(--shell-accent-primary)' }} />
            ) : (
              <RefreshCw size={14} strokeWidth={1.75} />
            )
          }
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid var(--shell-border-subtle)',
          borderRadius: 'var(--shell-radius-lg)',
          background: 'var(--shell-bg-surface)',
        }}
      >
        {loading && queues.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} sx={{ color: 'var(--shell-accent-primary)' }} />
          </Box>
        )}

        {!loading && queues.length === 0 && (
          <EmptyState
            title="Очередей пока нет"
            description="Нажмите «Синхронизировать очереди», чтобы подтянуть данные из Трекера."
          />
        )}

        {queues.length > 0 && (
          <>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                px: 1.5,
                py: 1,
                borderBottom: '1px solid var(--shell-border-subtle)',
                background: 'var(--shell-bg-subtle)',
                fontSize: 11,
                color: 'var(--shell-text-tertiary)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
              }}
            >
              {queues.length} очередей
            </Box>
            {queues.map((queue) => (
              <div
                key={queue.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '10px 16px',
                  borderBottom: '1px solid var(--shell-border-subtle)',
                  transition: 'background var(--shell-transition-fast)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--shell-bg-subtle)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: 'var(--shell-bg-subtle)',
                    color: 'var(--shell-text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                >
                  <List size={14} strokeWidth={1.75} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--shell-text-primary)',
                      lineHeight: 1.35,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {queue.name}
                  </div>
                  {queue.description && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: 'var(--shell-text-secondary)',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {queue.description}
                    </Typography>
                  )}
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--shell-text-tertiary)',
                      marginTop: 2,
                      fontFamily: 'var(--shell-font-mono, monospace)',
                    }}
                  >
                    {queue.key}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}

export default QueuesTab;
